import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import './Dashboard.css';
import OpenOrdersSummary from './OpenOrdersSummary';
import OpenPickerList from './OpenPickerList';
import { logout, getUserInfo } from '../utils/auth';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

function OpenOrdersDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { warehouse: warehouseParam } = useParams();
  const userInfo = getUserInfo();
  const [summaryData, setSummaryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter states
  const [countries, setCountries] = useState([]);
  const [orderTypes, setOrderTypes] = useState([]);
  const [country, setCountry] = useState('All');
  const [orderType, setOrderType] = useState('All');
  const [excludeHQ, setExcludeHQ] = useState(false);
  const [excludeCarriers, setExcludeCarriers] = useState(false);

  // Map order type codes to readable labels
  const orderTypeLabels = {
    'A': 'Assemblies',
    'C': 'Customer',
    'T': 'Transfers'
  };

  // Load filter values from URL on mount or warehouse change
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const urlCountry = searchParams.get('country');
    const urlOrderType = searchParams.get('orderType');
    const urlExcludeHQ = searchParams.get('excludeHQ');
    const urlExcludeCarriers = searchParams.get('excludeCarriers');

    console.log('URL params:', { urlCountry, urlOrderType, urlExcludeHQ, urlExcludeCarriers });
    console.log('Current filter state:', { country, orderType, excludeHQ, excludeCarriers });

    if (urlCountry) setCountry(urlCountry);
    if (urlOrderType) setOrderType(urlOrderType);
    if (urlExcludeHQ === 'true') setExcludeHQ(true);
    if (urlExcludeCarriers === 'true') setExcludeCarriers(true);
  }, [location.search]);

  // Load initial filter options
  useEffect(() => {
    fetchFilterOptions();
  }, []);

  // Load summary data when filters change
  useEffect(() => {
    if (!warehouseParam) {
      fetchSummaryData();

      const refreshInterval = setInterval(() => {
        fetchSummaryData();
      }, 60000); // 60 seconds

      return () => {
        clearInterval(refreshInterval);
      };
    }
  }, [country, orderType, excludeHQ, excludeCarriers, warehouseParam]);

  const fetchFilterOptions = async () => {
    try {
      const [countriesRes, orderTypesRes] = await Promise.all([
        axios.get(`${API_URL}/open-orders/countries`),
        axios.get(`${API_URL}/open-orders/order-types`)
      ]);

      setCountries(countriesRes.data || []);
      setOrderTypes(orderTypesRes.data || []);

      // Fetch data immediately
      fetchSummaryData();
    } catch (err) {
      console.error('Error fetching filter options:', err);
      // Even if filter options fail, try to fetch summary without filters
      fetchSummaryData();
    }
  };

  const fetchSummaryData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (country && country !== 'All') params.append('country', country);
      if (orderType && orderType !== 'All') params.append('orderType', orderType);
      if (excludeHQ) params.append('excludeHQ', 'true');
      if (excludeCarriers) params.append('excludeCarriers', 'true');

      const response = await axios.get(`${API_URL}/open-orders/summary?${params}`);
      setSummaryData(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching summary:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleWarehouseClick = (warehouse) => {
    console.log('handleWarehouseClick - current state:', { country, orderType, excludeCarriers });

    const params = new URLSearchParams();
    if (country && country !== 'All') params.append('country', country);
    if (orderType && orderType !== 'All') params.append('orderType', orderType);
    if (excludeHQ) params.append('excludeHQ', 'true');
    if (excludeCarriers) params.append('excludeCarriers', 'true');

    const queryString = params.toString();
    console.log('Navigating to:', `/open-orders/${encodeURIComponent(warehouse)}${queryString ? '?' + queryString : ''}`);
    navigate(`/open-orders/${encodeURIComponent(warehouse)}${queryString ? '?' + queryString : ''}`);
  };

  const handleBackToSummary = () => {
    navigate('/open-orders');
  };

  const handleLogout = () => {
    logout();
    // Force reload to clear all state and redirect to login
    window.location.href = '/login';
  };

  if (loading && !summaryData) {
    return <div className="dashboard-loading">Loading open orders...</div>;
  }

  if (error) {
    return <div className="dashboard-error">{error}</div>;
  }

  return (
    <div className="dashboard-container">
      <header className="dashboard-header">
        <div className="header-content">
          <h1>ITR America BI Reporting</h1>
          <div className="header-buttons">
            {userInfo && <span className="user-welcome">Welcome, {userInfo.fullName || userInfo.username}</span>}
            <button className="logout-btn" onClick={handleLogout}>Logout</button>
            <button className="back-home-btn" onClick={() => navigate('/')}>
              ← Back to Home
            </button>
          </div>
        </div>
        <h2>Open Orders - Current Status</h2>
      </header>

      {!warehouseParam && (
        <>
          <div className="filters-section">
            <div className="filter-group">
              <label>Country:</label>
              <select value={country} onChange={(e) => setCountry(e.target.value)}>
                <option value="All">All</option>
                {countries.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label>Order Type:</label>
              <select value={orderType} onChange={(e) => setOrderType(e.target.value)}>
                <option value="All">All</option>
                {orderTypes.map(type => (
                  <option key={type} value={type}>
                    {orderTypeLabels[type] || type}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={excludeHQ}
                  onChange={(e) => setExcludeHQ(e.target.checked)}
                />
                Exclude Customers: HQ
              </label>
            </div>

            <div className="filter-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={excludeCarriers}
                  onChange={(e) => setExcludeCarriers(e.target.checked)}
                />
                Exclude Carriers:{' '}
                <span className="carrier-tip" data-tip="Hold for Instructions">Z</span>
                {'/'}
                <span className="carrier-tip" data-tip="Will Call">WC</span>
                {'/'}
                <span className="carrier-tip" data-tip="Hold For Stock Truck">HFSK</span>
              </label>
            </div>
          </div>

          <OpenOrdersSummary
            data={summaryData}
            onWarehouseClick={handleWarehouseClick}
          />
        </>
      )}

      {warehouseParam && (
        <OpenPickerList
          warehouse={decodeURIComponent(warehouseParam)}
          country={country}
          orderType={orderType}
          excludeHQ={excludeHQ}
          excludeCarriers={excludeCarriers}
          onBack={handleBackToSummary}
        />
      )}
    </div>
  );
}

export default OpenOrdersDashboard;
